import { Button } from '@dawn/ui';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

import { sessionApi } from '@/lib/api';
import { useTenantLifecycleStore } from '@/stores/tenant-lifecycle';

/** How often the page auto-probes for reactivation. */
const RETRY_INTERVAL_MS = 60_000;

/**
 * Shown when the API blocks access with 403 TENANT_DEACTIVATED (TDEV-571 A4) —
 * the tenant is off-boarding. Explicit "contact support" messaging, not a login
 * redirect (re-authenticating won't restore access).
 *
 * Recoverable (Greptile P1): once support reactivates the tenant the block
 * clears without a manual reload — a "Check again" button plus a 60s auto-probe
 * hit a cheap authenticated endpoint (session ping, which goes through the gate);
 * on success we clear the flag and the app resumes. A still-blocked probe just
 * 403s again (the interceptor re-sets the flag — a no-op) and we keep waiting.
 *
 * Session expiry OUTRANKS the deactivated page (Greptile round 2): if the probe
 * 401s (the session expired while parked here), we clear the flag so this page
 * yields to the normal login flow — the interceptor has already flipped the
 * session store to expired, and clearing the flag lets SessionProvider redirect
 * to /login instead of trapping the user behind a forever-401 probe.
 */
export function TenantDeactivatedPage() {
  const clearDeactivated = useTenantLifecycleStore((s) => s.clearDeactivated);
  const [checking, setChecking] = useState(false);

  const checkAgain = useCallback(async () => {
    setChecking(true);
    try {
      await sessionApi.ping();
      // Reached the endpoint through the gate → the tenant is active again.
      clearDeactivated();
    } catch (error) {
      // A 401 means the session expired while parked here — session expiry
      // outranks the deactivated page. Clear the flag so the normal login flow
      // takes over (the interceptor already set the session to expired). A 403
      // (still deactivated) or offline leaves the flag set and we keep waiting.
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        clearDeactivated();
      }
    } finally {
      setChecking(false);
    }
  }, [clearDeactivated]);

  useEffect(() => {
    const id = setInterval(() => {
      void checkAgain();
    }, RETRY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [checkAgain]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <h1 className="text-4xl font-bold">Account deactivated</h1>
      <p className="mt-4 max-w-md text-lg text-muted-foreground">
        Your organization’s access to Dawn has been deactivated. If you believe this is a mistake,
        please contact support.
      </p>
      <div className="mt-6 flex items-center gap-4">
        <Button
          onClick={() => void checkAgain()}
          disabled={checking}
          data-testid="deactivated-check-again"
        >
          {checking ? 'Checking…' : 'Check again'}
        </Button>
        <a
          href="mailto:support@dawnsecurity.io"
          className="text-primary underline"
          data-testid="deactivated-contact-support"
        >
          Contact support
        </a>
      </div>
    </div>
  );
}
